import { useCallback } from "react";

import type { DiscountTargetType, DiscountTermType } from "@/lib/gql/__generated__/graphql";
import { CurrencyType, DiscountType } from "@/lib/gql/__generated__/graphql";

import * as DiscountShared from "../../../../_shared";

import type * as FormModelType from "../type";

type ViewModel = FormModelType.DiscountOrdinaryFormModel["ViewModel"];

export const useActions = (viewModel: ViewModel, onSetViewModel: (viewModel: ViewModel) => void) => {
	const {
		//
		// 公開にする
		onPublish,
		// 非公開にする
		onUnpublish,

		// 基本設定
		// ディスカウント名の変更
		onChangeName,
		// ディスカウント説明の変更
		onChangeDescription,
		// ディスカウントコードの変更
		onChangeDiscountCode,

		// ディスカウント対象の変更
		onChangeIsPlanDiscountEligible,
		// 部屋のディスカウント対象の変更
		onChangeIsRoomDiscountEligible,
		// オプションのディスカウント対象の変更
		onChangeIsOptionDiscountEligible,
		// 追加料金のディスカウント対象の変更
		onChangeIsAdditionalFeeDiscountEligible,
		// 追加設定
		// ディスカウント上限金額を設定しない
		onUnSettingDiscountPriceTotalLimit,
		// ディスカウント上限金額の変更
		onChangeDiscountPriceTotalLimit,
		// ディスカウントをプラン限定にするかの変更
		onChangeSaleTypeForPlan,
		// キャンセル不可のディスカウントにするかの変更
		onChangeIsUncancelable,
		// ディスカウントの利用可能期間を設定するかの変更
		onChangeSaleTypeForTerm,
		// 公開期間を変更
		onChangeSale,
		// 利用可能期間を変更
		onChangeStayable,
		// 利用可能曜日の変更
		onChangeStayableDayOfTheWeek,
	} = DiscountShared.Model.UseCase.useSharedActions(viewModel, onSetViewModel);

	/**
	 * ディスカウント方法の変更
	 */
	const onChangeDiscountType = useCallback(
		(discountType: DiscountType) => {
			if (discountType === DiscountType.Ratio) {
				onSetViewModel({
					...viewModel,
					discountType,
					ratio: {
						value: 10,
						isEdited: false,
						error: undefined,
					},
					static: undefined,
				});
			} else {
				onSetViewModel({
					...viewModel,
					discountType,
					ratio: undefined,
					static: {
						amount: {
							value: 1000,
							isEdited: false,
							error: undefined,
						},
						currencyType: {
							value: CurrencyType.Jpy,
							isEdited: false,
							error: undefined,
						},
					},
				});
			}
		},
		[onSetViewModel, viewModel],
	);
	/**
	 * ディスカウント金額の変更（一定割合）
	 */
	const onChangeDiscountWithRatio = useCallback(
		(ratio: number) => {
			onSetViewModel({
				...viewModel,
				ratio: { value: ratio, isEdited: viewModel.ratio?.isEdited ?? false, error: viewModel.ratio?.error },
			});
		},
		[onSetViewModel, viewModel],
	);
	/**
	 * ディスカウント金額の変更（固定金額）
	 */
	const onChangeDiscountWithStatic = useCallback(
		(amount: number) => {
			onSetViewModel({
				...viewModel,
				static: {
					amount: {
						value: amount,
						isEdited: viewModel.static?.amount.isEdited ?? false,
						error: viewModel.static?.amount.error,
					},
					currencyType: {
						value: CurrencyType.Jpy,
						isEdited: viewModel.static?.currencyType.isEdited ?? false,
						error: viewModel.static?.currencyType.error,
					},
				},
			});
		},
		[onSetViewModel, viewModel],
	);
	/**
	 * ディスカウント方法の変数の変更（顧客属性）
	 */
	const onChangeStaticTargetType = useCallback(
		(value: DiscountTargetType) => {
			onSetViewModel({
				...viewModel,
				staticTargetType: value,
			});
		},
		[onSetViewModel, viewModel],
	);
	/**
	 * ディスカウント方法の変数の変更（期間）
	 */
	const onChangeStaticTermType = useCallback(
		(value: DiscountTermType) => {
			onSetViewModel({
				...viewModel,
				staticTermType: value,
			});
		},
		[onSetViewModel, viewModel],
	);
	/**
	 * 最低価格の変更
	 */
	const onChangeResourceLowestPrice = useCallback(
		(amount: number) => {
			onSetViewModel({
				...viewModel,
				resourceLowestPrice: {
					...viewModel.resourceLowestPrice,
					amount: {
						...viewModel.resourceLowestPrice.amount,
						value: amount,
					},
				},
			});
		},
		[onSetViewModel, viewModel],
	);

	return {
		//
		// 公開にする
		onPublish,
		// 非公開にする
		onUnpublish,

		// 基本設定
		// ディスカウント名の変更
		onChangeName,
		// ディスカウント説明の変更
		onChangeDescription,
		// ディスカウントコードの変更
		onChangeDiscountCode,
		// ディスカウント対象の変更
		onChangeIsPlanDiscountEligible,
		// 部屋のディスカ�ント対象の変更
		onChangeIsRoomDiscountEligible,
		// オプションのディスカウント対象の変更
		onChangeIsOptionDiscountEligible,
		// 追加料金のディスカウント対象の変更
		onChangeIsAdditionalFeeDiscountEligible,
		// ディスカウント方法の変更
		onChangeDiscountType,
		// ディスカウント金額の変更（一定割合）
		onChangeDiscountWithRatio,
		// ディスカウント金額の変更（固定金額）
		onChangeDiscountWithStatic,
		// ディスカウント方法の変数の変更（顧客属性）
		onChangeStaticTargetType,
		// ディスカウント方法の変数の変更（期間）
		onChangeStaticTermType,

		// 追加設定
		// 最低価格の変更
		onChangeResourceLowestPrice,
		// ディスカウント上限金額を設定しない
		onUnSettingDiscountPriceTotalLimit,
		// ディスカウント上限金額の変更
		onChangeDiscountPriceTotalLimit,
		// ディスカウントをプラン限定にするかの変更
		onChangeSaleTypeForPlan,
		// キャンセル不可のディスカウントにするかの変更
		onChangeIsUncancelable,
		// ディスカウントの利用可能期間を設定するかの変更
		onChangeSaleTypeForTerm,
		// 公開期間を変更
		onChangeSale,
		// 利用可能期間を変更
		onChangeStayable,
		// 利用可能曜日の変更
		onChangeStayableDayOfTheWeek,
	};
};
